/**
 * Recursive tool call card for the real thread.
 *
 * Renders a tool_call content item as an expandable accordion card.
 * If the item has `children`, they are rendered recursively inside.
 * Supports `executionMode` ("loop" | "parallel" | "sequential") and
 * `iterationIndex` for labelling loop iterations.
 *
 * Designed to receive real streaming data from the GraphQL subscription.
 */

import { useLazyQuery } from "@apollo/client/react";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useEffect, useMemo, useState } from "react";
import { Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useEnumColor } from "@/hooks/useEnumColor";
import { icons } from "@/objects/@icons";
import { identifierAndTypenameToId } from "@/objects/@utils";
import type { NodeItem, ToolCallContent } from "./ToolCallCard.types";

// Types
export type { NodeItem, ToolCallContent } from "./ToolCallCard.types";

// Sub-components
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Icon } from "@/components/atoms/Icon";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { ToolCallCardChart } from "./ToolCallCardChart";
import { usePendingSession } from "@/components/organisms/PendingSession/PendingSession.store";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { isFileRow, isItemRow } from "./ToolCallCardItemRow";
import { ToolCallCardNodeLayout } from "./ToolCallCardNodeLayout";
import { ToolCallCardPendingStatus } from "./ToolCallCardPendingStatus";
import { ToolCallCardRawToggle } from "./ToolCallCardRaw";
import { ToolCallCardNodeReferences } from "./ToolCallCardReferences";
import {
  ToolCallCardBulkStats,
  ToolCallCardOutputStats,
  ToolCallCardStats,
} from "./ToolCallCardStats";
import { ToolCallCardStreaming } from "./ToolCallCardStreaming";
import { ToolCallCardTable } from "./ToolCallCardTable";
import { ToolCallCardActionThread } from "./ToolCallCardActionThread";
import { ToolCallCardNodeSummary } from "./ToolCallCardNodeSummary";
import { ToolCallCardChildList } from "./ToolCallCardChildList";
import {
  StuckRecordsProvider,
  usePendingChangeStatus,
  useStickyBarBottom,
  useStuckRecords,
} from "./ToolCallCard.hooks";
import {
  formatToolName,
  inferTypename,
  normalizeToolOutputForDisplay,
  renderInlineToolRow,
  toChartSpec,
  toolIcon,
} from "./ToolCallCard.tools";

// ── Main component ───────────────────────────────────────────────────────────

type ToolCallCardProps = {
  item: ToolCallContent;
  depth?: number;
  siblingCount?: number;
  threadIsStreaming?: boolean;
};

/**
 * A card's header stays in view under its turn's pinned prompt while the card's
 * contents scroll up behind it, and leaves with the card once they are past.
 * Painted in the card's colour so what scrolls under it does not show through.
 */
const STICKY_HEADER_CLASS =
  "sticky [top:var(--thread-pinned-prompt-bottom,0px)] z-2 bg-muted";

/**
 * The top of the record's box, drawn under the header while the record's title
 * is stuck beneath it (``data-record-stuck`` on the card).
 *
 * The box's own rounded top has scrolled away under the header by then, which
 * left the stuck title square against straight side borders. Nothing inside the
 * box can redraw it - the box clips its contents to inside its border - so the
 * header, which sits above the box, draws it in two layers one corner radius
 * tall, their edges on the box's (inset by the card's body padding):
 *
 * - ``::before`` fills the corners outside the curve in the card's colour,
 *   over the box's straight sides and the title's background. It has no radius
 *   of its own: a rounded box clips its background to the curve, which would
 *   leave exactly those corners unpainted.
 * - ``::after`` draws the border along the top and down the sides, rounded.
 */
const STUCK_RECORD_TOP_CLASS = cn(
  "[[data-record-stuck='true']>&]:before:content-[''] [[data-record-stuck='true']>&]:before:absolute [[data-record-stuck='true']>&]:before:top-full [[data-record-stuck='true']>&]:before:left-2 [[data-record-stuck='true']>&]:before:right-2 [[data-record-stuck='true']>&]:before:h-[calc(var(--radius)_-_2px)] [[data-record-stuck='true']>&]:before:box-border [[data-record-stuck='true']>&]:before:pointer-events-none",
  "[[data-record-stuck='true']>&]:after:content-[''] [[data-record-stuck='true']>&]:after:absolute [[data-record-stuck='true']>&]:after:top-full [[data-record-stuck='true']>&]:after:left-2 [[data-record-stuck='true']>&]:after:right-2 [[data-record-stuck='true']>&]:after:h-[calc(var(--radius)_-_2px)] [[data-record-stuck='true']>&]:after:box-border [[data-record-stuck='true']>&]:after:pointer-events-none",
  "[[data-record-stuck='true']>&]:before:[background-image:radial-gradient(circle_at_100%_100%,transparent_calc(calc(var(--radius)_-_2px)_-_0.5px),var(--muted)_calc(var(--radius)_-_2px)),radial-gradient(circle_at_0_100%,transparent_calc(calc(var(--radius)_-_2px)_-_0.5px),var(--muted)_calc(var(--radius)_-_2px))] [[data-record-stuck='true']>&]:before:[background-position:top_left,top_right] [[data-record-stuck='true']>&]:before:[background-size:calc(var(--radius)_-_2px)_calc(var(--radius)_-_2px)] [[data-record-stuck='true']>&]:before:bg-no-repeat",
  "[[data-record-stuck='true']>&]:after:[border-top:1px_solid_var(--border)] [[data-record-stuck='true']>&]:after:[border-left:1px_solid_var(--border)] [[data-record-stuck='true']>&]:after:[border-right:1px_solid_var(--border)] [[data-record-stuck='true']>&]:after:[border-bottom:none] [[data-record-stuck='true']>&]:after:rounded-tl-md [[data-record-stuck='true']>&]:after:rounded-tr-md",
);

function ToolCallCardInner({
  item,
  depth = 0,
  siblingCount,
  threadIsStreaming = false,
}: ToolCallCardProps) {
  const inline = renderInlineToolRow({ item });
  if (inline) return inline;

  const isWorkflowNodeEval = item.type === "node_eval";
  const isWorkflowNodeRunning =
    isWorkflowNodeEval &&
    (item.toolOutput as Record<string, unknown> | null | undefined)?.status ===
      "running";
  const isActionThreadNodeEval =
    isWorkflowNodeEval &&
    item.ruleNode?.__typename === "RuleNodeActionThreadObject";
  const summaryExternal = !!item.summaryShownExternally && isWorkflowNodeEval;

  const displayOutput = useMemo(() => {
    if (isWorkflowNodeRunning) return null;
    return normalizeToolOutputForDisplay(
      item.toolOutput,
      item.resolvedNodes ?? null,
    );
  }, [isWorkflowNodeRunning, item.toolOutput, item.resolvedNodes]);
  const titleText = formatToolName(
    item.toolName ?? "unknown",
    item.children,
    item.toolInput,
    item.status,
  );
  // The title stays on one line, so a long one is cut short; it says the rest
  // on hover, and only when it was cut.
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  // The record shown inside the card sticks its own title and tabs under this
  // header, so the card is told where the header ends.
  // A pixel is kept below it, for the record's top border it draws there while
  // the record's title is stuck (``STUCK_RECORD_TOP_CLASS``). Only a card has
  // that border: a record listed on its own sticks flush to the top.
  const headerRef = useStickyBarBottom(
    "--tool-call-header-bottom",
    "--thread-pinned-prompt-bottom",
    1,
  );
  // However many records the card is showing — its own, or every one a tool
  // worked through — its header draws the top of whichever are stuck under it.
  const stuckRecords = useStuckRecords();

  // Workflow node_eval rows are persisted snapshots, not live tool calls.
  // TRIGGER/SWITCH nodes legitimately have null toolOutput — don't treat
  // that as "still running".
  const isActive =
    item.isActiveToolCall ??
    (isWorkflowNodeRunning ||
      (!isWorkflowNodeEval && !displayOutput && !item.toolError));
  // A call the run never got to finish — the user stopped the turn — has
  // neither output nor error, but the row it left behind is closed. Without
  // reading that, such a card spins for good, in every browser that opens the
  // thread afterwards.
  const isDone = isWorkflowNodeRunning
    ? false
    : isWorkflowNodeEval ||
      !!displayOutput ||
      !!item.toolError ||
      item.status === "done";

  // Check for errors - both explicit toolError and failures in output
  const hasExplicitError = !!item.toolError;
  // What went wrong, in the orange of its Error chip.
  const errorColor = useEnumColor(EnumColors.ORANGE);
  const hasOutputFailures = useMemo(() => {
    if (!displayOutput) return false;
    const output = displayOutput;
    // Check for bulk operation failures
    const failed = typeof output.failed === "number" ? output.failed : 0;
    const successful =
      typeof output.successful === "number" ? output.successful : null;
    // Has failures if failed > 0, or if processed items but none successful
    if (failed > 0) return true;
    if (
      successful === 0 &&
      typeof output.processed === "number" &&
      output.processed > 0
    )
      return true;
    // Check for error in results array
    if (Array.isArray(output.results)) {
      return output.results.some((r: unknown) => {
        if (typeof r === "object" && r !== null) {
          const result = r as Record<string, unknown>;
          return (
            result.error ||
            (result.result &&
              typeof result.result === "object" &&
              (result.result as Record<string, unknown>).error)
          );
        }
        return false;
      });
    }
    return false;
  }, [displayOutput]);

  const hasError = hasExplicitError || hasOutputFailures;

  // Get failure count for display
  const failureCount = useMemo(() => {
    if (!displayOutput) return 0;
    const output = displayOutput;
    if (typeof output.failed === "number") return output.failed;
    return 0;
  }, [displayOutput]);

  // A chart belongs inside the call that asked for it, not adrift at the foot
  // of the turn: the card is what asked, and it opens to what it asked for.
  // The question is the call's own input, so the card can draw the ledger as
  // it stands rather than a picture kept from when the agent ran.
  // A call that came back with nothing to plot is a sentence, not an empty
  // pair of axes, so the chart goes only where there is something in it.
  const chartSpec = useMemo(() => {
    const spec = toChartSpec(item.toolName, item.toolInput);
    return spec && !hasError && displayOutput?.drawn !== false ? spec : null;
  }, [item.toolName, item.toolInput, displayOutput, hasError]);
  const hasChildren = (item.children?.length ?? 0) > 0;
  const hasItemRows = (item.children ?? []).some(isItemRow);
  // Records fill the card; the files it is reading are chips, and sit inside
  // its padding like anything else written on it.
  const hasRecordRows = (item.children ?? []).some(
    (child) => isItemRow(child) && !isFileRow(child),
  );
  const hasInput =
    item.toolInput &&
    Object.keys(item.toolInput).length > 0 &&
    !(isActionThreadNodeEval && item.text);
  const hasOutput = !!displayOutput;
  // Check if there are node references to display. ``related_items``
  // is *intentionally* ignored here — it carries cache-hydration
  // siblings (e.g. the parent Workflow that embeds the changed node)
  // that the AI tool emits so Apollo can refresh list-valued
  // fragments, but the user only cares about the primary item.
  const hasNodeReferences = useMemo(() => {
    if (!displayOutput) return false;
    const output = displayOutput;
    const hasItem = !!output.item;
    const hasItems = Array.isArray(output.items) && output.items.length > 0;
    return hasItem || hasItems;
  }, [displayOutput]);

  // Check if this is a singular operation (create, update, get, delete returning single item)
  const singularOutputItem = useMemo(() => {
    if (!displayOutput) return null;
    const output = displayOutput;
    // Must have exactly one item and no items array (or empty items array)
    const hasItem =
      output.item &&
      typeof output.item === "object" &&
      (output.item as NodeItem).identifier;
    const hasMultipleItems =
      Array.isArray(output.items) && output.items.length > 0;
    // Return the single item if it exists and there's no items array
    if (hasItem && !hasMultipleItems) {
      return output.item as NodeItem;
    }
    return null;
  }, [displayOutput]);

  // Load node when singular output (for accordion/spinner: don't expand until loaded)
  const base64Id = singularOutputItem
    ? identifierAndTypenameToId(
        singularOutputItem.identifier,
        inferTypename(singularOutputItem, item.toolName),
      )
    : "";
  const pendingSessionId = usePendingSession()?.pendingSessionId ?? null;
  const [
    loadNode,
    { loading: nodeLoading, data: nodeData, called: nodeCalled },
  ] = useLazyQuery(Queries.NODE_QUERY_WITH_PENDING_CHANGE, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  useEffect(() => {
    if (singularOutputItem?.identifier && base64Id) {
      // The same variables as the record inside the card, so both read one
      // cache entry for it.
      loadNode({
        variables: { id: base64Id, pendingSession: pendingSessionId },
      });
    }
  }, [singularOutputItem?.identifier, base64Id, loadNode, pendingSessionId]);
  // Initial load: the query has either not been kicked off yet, or it's the
  // first round trip with no data in hand. Once the round trip resolves —
  // whether to a node or to ``null`` (deleted / scoped out) — we stop
  // showing the header spinner so the card can settle into either the
  // node view or the "not found" empty state below.
  const initialNodeLoading = !!singularOutputItem && !nodeCalled;

  // Whether the change is still to be decided. The change itself says so
  // first - the same one the header's Confirmed / Rejected reads - since a
  // rejected create leaves no record to ask: read off the record alone, its
  // count stayed the green of a change still pending. Otherwise the record:
  // null pendingChange means it was decided; undefined means not yet loaded.
  const { isHistorical } = usePendingChangeStatus({
    node: nodeData?.node,
    toolName: item.toolName,
    pendingChangeIdentifier: item.pendingChangeIdentifier,
    pendingChangeType: item.pendingChangeType,
  });
  const isPending = isHistorical
    ? false
    : nodeData?.node
      ? (nodeData.node as Record<string, unknown>).pendingChange !== null
      : undefined;

  const hasExpandableContent =
    hasChildren || hasNodeReferences || hasError || !!chartSpec;
  // A call that makes one change shows it in its header (``+1``, Reject /
  // Confirm) once it is done; until then those hold their place as skeletons,
  // so they do not appear from nowhere and push the header about.
  const makesChange = /^(create|update|delete|bulk_create|bulk_update)_/.test(
    item.toolName ?? "",
  );
  // A bulk write's changes are its records, each opened on its own; it has no
  // single change for the header to confirm or reject.
  const bulkWrite = /^bulk_(create|update)_/.exec(item.toolName ?? "")?.[1];
  // The entity a single change is for: from what it returned once done, and
  // until then from what a create was asked to write under.
  const changeEntityIdentifier =
    makesChange && !bulkWrite
      ? (((singularOutputItem?.entity as { identifier?: string } | undefined)
          ?.identifier ??
          (item.toolInput?.entity_identifier as string | undefined)) as
          | string
          | undefined)
      : undefined;

  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const [bulkViewMode, setBulkViewMode] = useState<"summary" | "loop">("loop");

  // Reset toggle when the item's done state changes
  useEffect(() => {
    setManualToggle(null);
  }, [isDone]);

  // Results the agent chose to show are shown. Presenting is the deliberate
  // step that puts rows in front of the user — collapsed to a reference chip,
  // the records it found stayed hidden and the reply listed them in prose
  // instead, which is neither the records nor something you can open.
  const defaultExpanded =
    (displayOutput as Record<string, unknown> | undefined)?.present_to_user ===
      true ||
    // A call working through records is those records: shown from the moment
    // it starts on the first one, so the work can be watched rather than
    // opened for afterwards.
    hasItemRows ||
    // Records made together are shown together: the point of the call is the
    // records it wrote, each opening as it loads.
    bulkWrite === "create" ||
    // A chart is the whole of what its call is for.
    !!chartSpec;
  const showExpanded = manualToggle !== null ? manualToggle : defaultExpanded;
  const isClickable = isDone && hasExpandableContent;

  const mode = item.executionMode;
  const isLoop = mode === "loop";
  const isParallel = mode === "parallel";

  // Build iteration label
  const iterLabel =
    item.iterationIndex != null && siblingCount
      ? `${item.iterationIndex + 1}/${siblingCount}`
      : null;

  // Mode label
  const childToolCalls =
    item.children?.filter((c) => c.type === "tool_call") ?? [];
  const modeLabel = isLoop
    ? `↻ ${childToolCalls.length} iterations`
    : isParallel
      ? `⫘ ${childToolCalls.length} parallel`
      : childToolCalls.length > 1
        ? `${childToolCalls.length} steps`
        : null;

  // for_each_agent - iterations render as separate cards inside
  const isForEach = item.toolName === "for_each_agent";

  // An iteration's title leads with where it falls in its loop; a for_each's
  // own header does not.
  const iterPrefix = !isForEach && iterLabel ? `${iterLabel} · ` : "";
  const title = (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid="tool-call-title"
          onMouseEnter={(event) =>
            setIsTitleTruncated(
              event.currentTarget.scrollWidth > event.currentTarget.clientWidth,
            )
          }
          className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {/* What the call is about, before what it is doing. */}
          <span className="inline-flex items-center mr-1.5 [vertical-align:-2px] text-muted-foreground/80">
            <Icon ReactIcon={toolIcon(item.toolName)} size="sm" />
          </span>
          {iterPrefix}
          {isWorkflowNodeEval ? (
            <ToolCallCardNodeSummary item={item} />
          ) : (
            titleText
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start">
        {isTitleTruncated ? `${iterPrefix}${titleText}` : ""}
      </TooltipContent>
    </Tooltip>
  );

  // Group children by iterationIndex for for_each tools
  const iterationGroups = useMemo(() => {
    if (!isForEach || !item.children)
      return new Map<number, ToolCallContent[]>();

    const groups = new Map<number, ToolCallContent[]>();
    for (const child of item.children) {
      const idx = child.iterationIndex ?? 0;
      if (!groups.has(idx)) {
        groups.set(idx, []);
      }
      groups.get(idx)!.push(child);
    }
    return groups;
  }, [isForEach, item.children]);

  // Get the expected iteration count from tool input
  const expectedIterationCount = useMemo(() => {
    if (!isForEach || !item.toolInput) return iterationGroups.size;
    const limit = item.toolInput.source_limit as number | undefined;
    return limit ?? iterationGroups.size;
  }, [isForEach, item.toolInput, iterationGroups.size]);

  // Collect all affected items from child tool outputs for for_each display
  const forEachAffectedItems = useMemo(() => {
    if (!isForEach) return [];

    const items: NodeItem[] = [];
    const seenIdentifiers = new Set<string>();

    // Helper to add an item if not seen
    const addItem = (nodeItem: NodeItem) => {
      if (nodeItem.identifier && !seenIdentifiers.has(nodeItem.identifier)) {
        seenIdentifiers.add(nodeItem.identifier);
        items.push(nodeItem);
      }
    };

    // First, check the parent's toolOutput for results (for_each_agent style)
    if (item.toolOutput) {
      const output = item.toolOutput as Record<string, unknown>;
      // Check 'results' array (for_each_agent format)
      if (Array.isArray(output.results)) {
        for (const result of output.results as Record<string, unknown>[]) {
          // Results might have item_identifier and item_name
          if (result.item_identifier) {
            addItem({
              identifier: result.item_identifier as string,
              name: result.item_name as string | undefined,
              __typename: result.item_type as string | undefined,
            } as NodeItem);
          }
          // Or might have nested result.item
          if (result.result && typeof result.result === "object") {
            const innerResult = result.result as Record<string, unknown>;
            if (innerResult.item && typeof innerResult.item === "object") {
              addItem(innerResult.item as NodeItem);
            }
          }
        }
      }
      // Also check 'items' array on parent
      if (Array.isArray(output.items)) {
        for (const nodeItem of output.items as NodeItem[]) {
          addItem(nodeItem);
        }
      }
    }

    // Then collect from children
    if (item.children) {
      const collectItems = (children: ToolCallContent[]) => {
        for (const child of children) {
          if (child.toolOutput) {
            const output = child.toolOutput as Record<string, unknown>;
            // Collect from 'item' (single result)
            if (output.item && typeof output.item === "object") {
              addItem(output.item as NodeItem);
            }
            // Collect from 'items' (array result)
            if (Array.isArray(output.items)) {
              for (const nodeItem of output.items as NodeItem[]) {
                addItem(nodeItem);
              }
            }
          }
          // Recurse into nested children
          if (child.children) {
            collectItems(child.children);
          }
        }
      };

      collectItems(item.children);
    }

    return items;
  }, [isForEach, item.children, item.toolOutput]);

  // Check if for_each has affected items to show
  const hasForEachResults = isForEach && forEachAffectedItems.length > 0;

  // Get the tool name from first child for inferring typename, or from toolInput/affected items
  const forEachChildToolName = useMemo(() => {
    if (!isForEach) return null;

    // Try from children first
    if (item.children) {
      const firstToolCall = item.children.find(
        (c) => c.type === "tool_call" && c.toolName,
      );
      if (firstToolCall?.toolName) return firstToolCall.toolName;
    }

    // Try from toolInput service_name (e.g., "contact" → "query_contact")
    if (item.toolInput?.service_name) {
      return `query_${String(item.toolInput.service_name)}`;
    }

    // Try from first affected item's __typename (e.g., "ContactObject" → "query_contact")
    if (forEachAffectedItems.length > 0 && forEachAffectedItems[0].__typename) {
      const typename = forEachAffectedItems[0].__typename;
      // "ContactObject" → "contact"
      const serviceName = typename
        .replace(/Object$/, "")
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        .toLowerCase();
      return `query_${serviceName}`;
    }

    return null;
  }, [isForEach, item.children, item.toolInput, forEachAffectedItems]);

  // For "for_each" tools, render with iterations as separate cards inside the parent
  if (isForEach) {
    const iterationCount = Math.max(
      iterationGroups.size,
      expectedIterationCount,
    );

    return (
      <div className="w-full mt-1 mb-1">
        <div className="rounded-lg [border-width:1px] [border-style:solid] border-border bg-muted shadow-sm overflow-clip">
          {/* Header */}
          <div
            ref={headerRef}
            onClick={() =>
              (hasChildren || hasForEachResults || hasError) &&
              setManualToggle((p) => !(p ?? showExpanded))
            }
            className={cn(
              "sticky top-(--thread-pinned-prompt-bottom,0px) z-2 bg-muted flex items-center gap-1.5 pl-2 pr-2 pt-2 pb-2",
              hasChildren || hasForEachResults || hasError
                ? "cursor-pointer"
                : "cursor-default",
              showExpanded
                ? "[border-bottom-width:1px] [border-bottom-style:solid]"
                : "[border-bottom-width:initial] [border-bottom-style:none]",
              "border-border",
              (hasChildren || hasForEachResults || hasError) &&
                "hover:bg-accent",
              "[transition:background-color_0.15s]",
            )}
          >
            <Typography
              level="body-md"
              className="flex-1 min-w-0 flex items-center gap-1"
            >
              {title}
              {/* Stats in order: read (→), create (+), modify (~), delete (-) */}
              {isDone && (
                <ToolCallCardOutputStats
                  toolName={item.toolName}
                  toolOutput={displayOutput}
                  toolError={item.toolError}
                  isPending={isPending}
                />
              )}
              {isDone && (
                <ToolCallCardBulkStats children={item.children ?? []} />
              )}
            </Typography>

            {isDone ? (
              <>
                {hasError && (
                  <SimpleEnum
                    label="Error"
                    enumColor={EnumColors.ORANGE}
                    size="sm"
                  />
                )}
                {/* Summary/Loop toggle in header */}
                {hasForEachResults && hasChildren && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-0.5"
                  >
                    <Typography
                      onClick={() => setBulkViewMode("summary")}
                      level="body-xs"
                      className={cn(
                        "pl-1 pr-1 text-[10px]",
                        "cursor-pointer",
                        bulkViewMode === "summary"
                          ? "text-foreground"
                          : "text-muted-foreground/80",
                        "hover:text-foreground",
                      )}
                    >
                      Summary
                    </Typography>
                    <Typography
                      level="body-xs"
                      className="text-muted-foreground/80 text-[10px]"
                    >
                      /
                    </Typography>
                    <Typography
                      onClick={() => setBulkViewMode("loop")}
                      level="body-xs"
                      className={cn(
                        "pl-1 pr-1 text-[10px]",
                        "cursor-pointer",
                        bulkViewMode === "loop"
                          ? "text-foreground"
                          : "text-muted-foreground/80",
                        "hover:text-foreground",
                      )}
                    >
                      Loop
                    </Typography>
                  </div>
                )}
                {(hasChildren || hasForEachResults || hasError) && (
                  <motion.div
                    animate={{ rotate: showExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      display: "flex",
                      fontSize: "10px",
                      color: "inherit",
                      opacity: 0.4,
                    }}
                  >
                    <Icon ReactIcon={icons.chevronDown} />
                  </motion.div>
                )}
              </>
            ) : (
              <Spinner />
            )}

            {/* Show Raw toggle in header for for_each */}
            {isDone && (item.toolInput || displayOutput || item.toolError) && (
              <ToolCallCardRawToggle
                toolInput={item.toolInput}
                toolOutput={displayOutput ?? item.toolOutput}
                toolError={item.toolError}
              />
            )}
          </div>

          {/* Expanded content - either summary or loop based on toggle */}
          {(hasChildren || hasForEachResults || hasError || isActive) && (
            <AnimatePresence initial={false}>
              {showExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  {/* Error display */}
                  {hasError && (
                    <div
                      className="pl-3 pr-3 pt-2 pb-2 [border-bottom-width:1px] [border-bottom-style:solid]"
                      style={{
                        backgroundColor: errorColor.backgroundColor,
                        borderColor: errorColor.borderColor,
                      }}
                    >
                      <Typography
                        level="body-xs"
                        className="font-mono whitespace-pre-wrap [word-break:break-word]"
                        style={{ color: errorColor.color }}
                      >
                        {item.toolError
                          ? typeof item.toolError === "string"
                            ? item.toolError
                            : JSON.stringify(item.toolError, null, 2)
                          : failureCount > 0
                            ? `${failureCount} item${failureCount === 1 ? "" : "s"} failed`
                            : "Operation failed"}
                      </Typography>
                    </div>
                  )}

                  {/* Summary view - affected items table */}
                  {bulkViewMode === "summary" && hasForEachResults && (
                    <ToolCallCardTable
                      items={forEachAffectedItems}
                      toolName={forEachChildToolName}
                    />
                  )}

                  {/* Loop view - iterations with bullet list */}
                  {bulkViewMode === "loop" && (
                    <div className="pl-2 pr-2 pt-2 pb-2 w-full min-w-0 overflow-hidden bg-card">
                      {Array.from(iterationGroups.entries())
                        .sort(([a], [b]) => a - b)
                        .map(([iterIdx, children], index, arr) => {
                          const isLast = index === arr.length - 1;
                          return (
                            <div
                              key={iterIdx}
                              className={cn(isLast ? "pb-0" : "pb-2")}
                            >
                              <Typography
                                level="body-xs"
                                className="font-mono text-[10px] [font-weight:600] text-muted-foreground/80 mb-1 [line-height:18px]"
                              >
                                Iteration {iterIdx + 1} of {iterationCount}
                              </Typography>

                              <div className="flex flex-col gap-1 w-full max-w-full">
                                {children.map((child) => {
                                  if (child.type === "tool_call") {
                                    return (
                                      <ToolCallCard
                                        key={child.identifier}
                                        item={child}
                                        depth={depth + 1}
                                        threadIsStreaming={threadIsStreaming}
                                      />
                                    );
                                  }
                                  if (
                                    child.type === "text" &&
                                    (child.text || child.streamingAgentText)
                                  ) {
                                    return (
                                      <ToolCallCardStreaming
                                        key={child.identifier}
                                        text={
                                          child.streamingAgentText ??
                                          child.text ??
                                          ""
                                        }
                                        isStreaming={child.isStreaming}
                                      />
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    );
  }

  // Workflow agent step: nested sub-execution output (summary rendered as message).
  if (summaryExternal && isWorkflowNodeRunning && !hasChildren && !hasError) {
    return null;
  }

  if (
    summaryExternal &&
    !isActionThreadNodeEval &&
    !hasExpandableContent &&
    !isWorkflowNodeRunning &&
    !hasError
  ) {
    return null;
  }

  if (isActionThreadNodeEval) {
    return (
      <ToolCallCardActionThread
        item={item}
        depth={depth}
        threadIsStreaming={threadIsStreaming}
        ToolCallCard={ToolCallCardInner}
      />
    );
  }

  // Regular tool calls (not for_each)
  return (
    <StuckRecordsProvider value={stuckRecords.report}>
      <div
        className={cn(
          "w-full",
          summaryExternal ? "mt-0 mb-0" : "mt-1 mb-1",
          "max-w-full",
        )}
      >
        <div
          data-tool-call-card
          data-record-stuck={String(stuckRecords.anyStuck)}
          // ``clip`` rounds the corners without making the card a scroll
          // container, which would keep its header from sticking.
          className={cn(
            summaryExternal
              ? "max-w-full"
              : "rounded-lg [border-width:1px] [border-style:solid] border-border bg-muted shadow-sm overflow-clip",
            "max-w-full",
          )}
        >
          {/* Header */}
          {!summaryExternal && (
            <div
              ref={headerRef}
              onClick={() =>
                isClickable && setManualToggle((p) => !(p ?? showExpanded))
              }
              className={cn(
                "flex items-center gap-1.5 pl-2 pr-2 pt-2 pb-2 [transition:background-color_0.15s] border-border",
                STICKY_HEADER_CLASS,
                STUCK_RECORD_TOP_CLASS,
                isClickable ? "cursor-pointer" : "cursor-default",
              )}
            >
              <div className="flex items-center gap-1.5 flex-1 min-w-0 pl-1">
                {changeEntityIdentifier && (
                  <BoxForEntity
                    identifier={changeEntityIdentifier}
                    representation="character"
                    size="sm"
                  />
                )}
                <Typography
                  level="body-md"
                  className="flex-1 min-w-0 flex items-center gap-1"
                >
                  {title}
                  {isDone ? (
                    <ToolCallCardOutputStats
                      toolName={item.toolName}
                      toolOutput={displayOutput}
                      toolError={item.toolError}
                      isPending={isPending}
                    />
                  ) : (
                    makesChange && <ToolCallCardOutputStats isLoading />
                  )}
                </Typography>

                {/* Status */}
                {isDone ? (
                  <>
                    <ToolCallCardStats children={item.children ?? []} />
                    {hasError && (
                      <SimpleEnum
                        label="Error"
                        enumColor={EnumColors.ORANGE}
                        size="sm"
                      />
                    )}
                    {singularOutputItem && (
                      <ToolCallCardPendingStatus
                        node={nodeData?.node}
                        toolName={item.toolName}
                        pendingChangeIdentifier={item.pendingChangeIdentifier}
                        pendingChangeType={item.pendingChangeType}
                        isLoading={
                          initialNodeLoading || (nodeLoading && !nodeData)
                        }
                      />
                    )}
                    {hasExpandableContent && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Expand"
                      >
                        <motion.div
                          animate={{
                            rotate: showExpanded ? 180 : 0,
                            transformOrigin: "center",
                            translateY: showExpanded ? "-2px" : "0px",
                          }}
                          transition={{ duration: 0.2 }}
                          // style={{
                          //   display: "flex",
                          //   fontSize: "10px",
                          //   color: "inherit",
                          //   opacity: 0.4,
                          // }}
                        >
                          <Icon ReactIcon={icons.chevronDown} />
                        </motion.div>
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {makesChange && !bulkWrite && (
                      <ToolCallCardPendingStatus
                        node={undefined}
                        toolName={item.toolName}
                        isLoading
                      />
                    )}
                    {/* A call that makes a change says it is working through
                      its skeletons and holds its chevron in place; any other
                      call has nothing to hold, so it spins. So does a bulk
                      create: it opens to the records it makes, and has none
                      to show until they are made. */}
                    {makesChange && bulkWrite !== "create" ? (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Expand"
                        disabled
                      >
                        <Icon ReactIcon={icons.chevronDown} />
                      </Button>
                    ) : (
                      <Spinner />
                    )}
                  </>
                )}
              </div>

              {/* Show Raw toggle in header */}
              {isDone ? (
                (item.toolInput || displayOutput || item.toolError) && (
                  <ToolCallCardRawToggle
                    toolInput={item.toolInput}
                    toolOutput={displayOutput ?? item.toolOutput}
                    toolError={item.toolError}
                  />
                )
              ) : (
                <ToolCallCardRawToggle isLoading />
              )}
            </div>
          )}

          {/* Expandable content: table, inputs, outputs, children. Opened
              only once there is something in it: a card open from the start
              drew its box empty, as a bare line, until its call came back. */}
          <AnimatePresence initial={false}>
            {showExpanded && hasExpandableContent && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                // The record's box sits inside this padding, and the header
                // draws its stuck top at the same inset (STUCK_RECORD_TOP_CLASS).
                className="w-full max-w-full box-border pl-2 pr-2"
              >
                <div
                  data-testid="tool-call-content"
                  // Clipped, not a scroll container: the record inside sticks
                  // its title and tabs to the thread as it scrolls.
                  className="[border-width:1px] [border-style:solid] border-border rounded-md overflow-clip mb-2 bg-background"
                >
                  {/* Error display */}
                  {hasError && (
                    <div
                      className="pl-3 pr-3 pt-2 pb-2 [border-bottom-width:1px] [border-bottom-style:solid]"
                      style={{
                        backgroundColor: errorColor.backgroundColor,
                        borderColor: errorColor.borderColor,
                      }}
                    >
                      <Typography
                        level="body-xs"
                        className="font-mono whitespace-pre-wrap [word-break:break-word]"
                        style={{ color: errorColor.color }}
                      >
                        {item.toolError
                          ? typeof item.toolError === "string"
                            ? item.toolError
                            : JSON.stringify(item.toolError, null, 2)
                          : failureCount > 0
                            ? `${failureCount} item${failureCount === 1 ? "" : "s"} failed`
                            : "Operation failed"}
                      </Typography>
                    </div>
                  )}

                  {/* Results - inline NodeLayout for singular operations,
                      references for multiple. The records a call worked
                      through are already its rows; naming them again here
                      would list every one of them twice. */}
                  {!hasItemRows &&
                    isDone &&
                    displayOutput &&
                    (singularOutputItem ? (
                      <div
                        data-node-identifier={singularOutputItem.identifier}
                        className="w-full min-w-0 overflow-clip"
                      >
                        <ToolCallCardNodeLayout
                          item={singularOutputItem}
                          toolName={item.toolName}
                          pendingChangeIdentifier={item.pendingChangeIdentifier}
                          pendingChangeType={item.pendingChangeType}
                        />
                      </div>
                    ) : (
                      <ToolCallCardNodeReferences
                        toolOutput={displayOutput}
                        toolName={item.toolName}
                      />
                    ))}

                  {chartSpec && <ToolCallCardChart spec={chartSpec} />}

                  {/* Children (nested tool calls and text) */}
                  {hasChildren && (
                    <div
                      // Records the tool worked through are the card's own
                      // content, like the one record a single call shows: they
                      // run to its edges rather than sitting in a tray of
                      // nested calls.
                      className={cn(
                        hasRecordRows
                          ? "flex flex-col"
                          : "pl-2 pr-2 pt-1.5 pb-1.5 flex flex-col gap-1",
                      )}
                    >
                      <ToolCallCardChildList
                        children={item.children}
                        depth={depth}
                        threadIsStreaming={threadIsStreaming}
                        ToolCallCard={ToolCallCardInner}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </StuckRecordsProvider>
  );
}

// Custom comparison for ToolCallCard
function areToolCallsEqual(
  prevProps: ToolCallCardProps,
  nextProps: ToolCallCardProps,
): boolean {
  // Check depth and siblingCount
  if (prevProps.depth !== nextProps.depth) return false;
  if (prevProps.siblingCount !== nextProps.siblingCount) return false;
  if (prevProps.threadIsStreaming !== nextProps.threadIsStreaming) return false;

  // Check item identity
  if (prevProps.item.identifier !== nextProps.item.identifier) return false;

  // Check active/done state
  if (prevProps.item.isActiveToolCall !== nextProps.item.isActiveToolCall)
    return false;

  // Check tool output and error (main content that changes)
  if (prevProps.item.toolInput !== nextProps.item.toolInput) return false;
  if (prevProps.item.toolOutput !== nextProps.item.toolOutput) return false;
  if (prevProps.item.resolvedNodes !== nextProps.item.resolvedNodes)
    return false;
  if (prevProps.item.toolError !== nextProps.item.toolError) return false;

  // Check streaming text and pending change identifier
  if (prevProps.item.streamingAgentText !== nextProps.item.streamingAgentText)
    return false;
  if (
    prevProps.item.pendingChangeIdentifier !==
    nextProps.item.pendingChangeIdentifier
  )
    return false;

  // Check children length
  const prevChildLen = prevProps.item.children?.length ?? 0;
  const nextChildLen = nextProps.item.children?.length ?? 0;
  if (prevChildLen !== nextChildLen) return false;

  // Compare children identifiers and outputs
  if (nextChildLen > 0) {
    const prevChildren = prevProps.item.children!;
    const nextChildren = nextProps.item.children!;
    for (let i = 0; i < nextChildLen; i++) {
      if (prevChildren[i]?.identifier !== nextChildren[i]?.identifier)
        return false;
      if (prevChildren[i]?.toolInput !== nextChildren[i]?.toolInput)
        return false;
      if (prevChildren[i]?.toolOutput !== nextChildren[i]?.toolOutput)
        return false;
      if (prevChildren[i]?.toolError !== nextChildren[i]?.toolError)
        return false;
      if (
        prevChildren[i]?.isActiveToolCall !== nextChildren[i]?.isActiveToolCall
      )
        return false;
    }
  }

  return true;
}

export const ToolCallCard = memo(ToolCallCardInner, areToolCallsEqual);
